import { useTranslation } from 'react-i18next';

import { IMAGE_CONSTANTS } from '@/constants';
import { useAppSelector } from '@/store';
import { TASK_STATUS } from '@/types';
import { getPriorityInfo } from '@/utils';
import { cn } from '@/utils/cn';

import { TaskActionButtons } from './TaskActionButtons';

import type { TaskDetail, TaskTabType } from '@/types';

interface TaskCardProps {
  task: TaskDetail;
  tab: TaskTabType;
  isOperating?: boolean | undefined;
  onAccept?: (() => void) | undefined;
  onStart?: (() => void) | undefined;
  onCancel?: (() => void) | undefined;
  onView?: (() => void) | undefined;
}

/**
 * Xamarin NewTaskItemViewCell.xaml header layout:
 *
 * Grid Row=0, HeightRequest=55, ColumnSpacing=0
 * Background: TaskPriorityColorConverter (bold color fills entire header)
 *
 * 3 columns: [Auto: icon] [*: source -> arrow -> dest] [Auto: priorityName]
 *
 * Column 0: Frame Padding="16,0,10,0"
 *   - TaskPriorityToTaskIconConverter icon, HeightRequest=30
 *     Move: always move_task.svg
 *     Audit Critical: audit_task_critical.svg
 *     Audit High: audit_task_high.svg
 *     Audit Standard: audit_task.svg
 *
 * Column 1: StackLayout Horizontal
 *   - Source frame (Width=60 phone): TaskTypeToSourceNameConverter
 *     Move: MoveSourceName, Audit: CurrentLoggedInUser
 *     PoppinsBold 12px, White, TailTruncation
 *   - Arrow frame (Width=40, Margin=8): arrow.svg, HeightRequest=20
 *   - Dest frame (Width=40 phone): Move="DL****" or dest, Audit=AuditLocationName
 *     PoppinsBold 12px, White, NoWrap
 *
 * Column 2: Frame Margin="-10,0,0,0"
 *   - PriorityName label, PoppinsBold 12px, White, Margin="0,0,20,0"
 *
 * Color mapping (TaskPriorityColorConverter):
 *   Critical:       #BE0000
 *   High:           #E38C25
 *   Move Standard:  #E38C25
 *   Audit Standard: #273F94
 *
 * Border: TaskBorderColor #A1CDE8, CornerRadius=10, Padding=1
 */

/** Get the header bg color matching Xamarin TaskPriorityColorConverter */
function getHeaderBgColor(task: TaskDetail, priorityName: string): string {
  if (priorityName === 'Critical') return '#BE0000';
  if (priorityName === 'High') return '#E38C25';
  if (task.TaskType === 'Move') return '#E38C25';
  return '#273F94'; // Audit Standard
}

/** Get the task icon matching Xamarin TaskPriorityToTaskIconConverter */
function getTaskIcon(task: TaskDetail, priorityName: string): string {
  if (task.TaskType === 'Move') return IMAGE_CONSTANTS.moveTask;
  // Audit: priority-specific icons
  if (priorityName === 'Critical') return IMAGE_CONSTANTS.auditTaskCritical;
  if (priorityName === 'High') return IMAGE_CONSTANTS.auditTaskHigh;
  return IMAGE_CONSTANTS.auditTask;
}

/** Get source text matching Xamarin TaskTypeToSourceNameConverter */
function getSourceName(task: TaskDetail, currentUserName: string): string {
  if (task.TaskType === 'Move') return task.MoveSourceName || '';
  // Audit: CurrentLoggedInUser (set by ViewModel to logged-in user's name)
  return currentUserName;
}

/** Get destination text matching Xamarin converters */
function getDestinationName(task: TaskDetail): string {
  if (task.TaskType === 'Move') {
    // MoveTaskTypeToDestinationNameConverter: show actual destination only if Complete
    return task.Status === TASK_STATUS.COMPLETED ? task.MoveDestinationName || '' : 'DL****';
  }
  // Audit: TaskTypeToDestinationNameConverter -> AuditLocationName
  return task.AuditLocationName || '';
}

export function TaskCard({
  task,
  tab,
  isOperating,
  onAccept,
  onStart,
  onCancel,
  onView,
}: TaskCardProps) {
  const { t } = useTranslation();
  // Xamarin: CurrentLoggedInUser = user.FirstName + " " + user.LastName
  const user = useAppSelector((s) => s.user.user);
  const currentUserName = user ? `${user.first_name} ${user.last_name}`.trim() : '';

  const priorityInfo = getPriorityInfo(task.Priority);
  const priorityName = priorityInfo.name;

  const headerBg = getHeaderBgColor(task, priorityName);
  const taskIcon = getTaskIcon(task, priorityName);
  const sourceName = getSourceName(task, currentUserName);
  const destinationName = getDestinationName(task);

  return (
    <div
      className={cn('overflow-hidden', isOperating && 'opacity-60 pointer-events-none')}
      /* Xamarin: outer Frame Padding=1, CornerRadius=10, BorderColor=TaskBorderColor #A1CDE8 */
      style={{ border: '1px solid #A1CDE8', borderRadius: 10 }}
    >
      {/* -- Header -- Xamarin: Grid Row=0, HeightRequest=55, ColumnSpacing=0
           3 columns: [Auto: icon frame] [*: source+arrow+dest] [Auto: priorityName] */}
      <div className="flex items-stretch h-[55px]" style={{ backgroundColor: headerBg }}>
        {/* Column 0: Task icon -- Xamarin: Frame Padding="16,0,10,0", icon HeightRequest=30 */}
        <div className="flex items-center shrink-0 pl-4 pr-[10px]">
          <img src={taskIcon} alt={task.TaskType} className="h-[30px] w-auto" />
        </div>

        {/* Column 1: Source + Arrow + Destination -- Xamarin: StackLayout Horizontal, flex=* */}
        <div className="flex items-center flex-1 min-w-0">
          {/* Source frame -- Xamarin: WidthRequest=60 (phone), Padding=5, PoppinsBold 12px, White, TailTruncation */}
          <div className="w-[60px] shrink-0 px-[5px]">
            <span className="block text-white font-poppins font-bold text-xs truncate">
              {sourceName}
            </span>
          </div>

          {/* Arrow frame -- Xamarin: WidthRequest=40, Margin=8, arrow.svg HeightRequest=20 */}
          <div className="w-10 shrink-0 mx-2 flex items-center justify-center">
            <img src={IMAGE_CONSTANTS.arrow} alt="" className="h-5 w-auto" />
          </div>

          {/* Destination frame -- Xamarin: WidthRequest=40 (phone), Padding=5, PoppinsBold 12px, White, NoWrap */}
          <div className="w-10 shrink-0 px-[5px]">
            <span className="block text-white font-poppins font-bold text-xs whitespace-nowrap">
              {destinationName}
            </span>
          </div>
        </div>

        {/* Column 2: PriorityName -- Xamarin: Frame Margin="-10,0,0,0", Label Margin="0,0,20,0"
             PoppinsBold 12px, White, NoWrap */}
        <div className="flex items-center shrink-0 pr-5 -ml-[10px]">
          <span className="text-white font-poppins font-bold text-xs whitespace-nowrap">
            {priorityName}
          </span>
        </div>
      </div>

      {/* -- Body -- Xamarin: Grid Row=1, Padding="20,10"
           Left: "Trailer Name | Carrier Name" label + TrailerName | CarrierCompanyName (or "Empty") + ExceptionText
           Right: Action buttons (Cancel/Start/Accept icons per tab)
      */}
      <div
        role="button"
        tabIndex={0}
        className="bg-white px-5 py-[10px] cursor-pointer"
        onClick={() => onView?.()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onView?.();
          }
        }}
      >
        <div className="flex items-start justify-between gap-3">
          {/* Left column */}
          <div className="flex-1 min-w-0">
            {/* "Trailer Name | Carrier Name" + Priority badge -- Xamarin: TaskItemActionMobileViewCell
                 Horizontal StackLayout with Spacing=8: Label + Frame CornerRadius=20 */}
            <div className="flex items-center gap-2">
              <p className="text-[15px] font-poppins font-normal text-task-frame leading-tight">
                {t('dashboard.trailerAndCarrier')}
              </p>

              {/* Xamarin: Priority badge pill — visible only for Critical & High */}
            </div>

            {/* TrailerName | CarrierCompanyName -- Xamarin: PoppinsSemiBold 15px, TaskDetailsTextColor #243743 */}
            {task.TrailerName ? (
              <p className="text-[15px] font-poppins font-semibold text-task-details truncate leading-tight mt-0.5">
                {task.TrailerName} | {task.CarrierCompanyName || ''}
              </p>
            ) : (
              <p className="text-[15px] font-poppins font-semibold text-task-details leading-tight mt-0.5">
                {t('common.empty')}
              </p>
            )}

            {/* Exception text -- Xamarin: PoppinsRegular 12px, CriticalPriorityDarkColor */}
            {task.ExceptionCode && task.ExceptionText && (
              <p className="text-xs font-poppins text-[#FC3B3B] mt-1">{task.ExceptionText}</p>
            )}
          </div>

          {/* Right column: Action buttons */}
          <div className="flex items-center shrink-0">
            <TaskActionButtons
              tab={tab}
              task={task}
              isOperating={isOperating}
              onAccept={onAccept}
              onStart={onStart}
              onCancel={onCancel}
              onView={onView}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
